<script setup lang="ts">
import { computed, nextTick, onMounted, watch, onUnmounted, ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import { AppFirstLevelPanel, AppFadeInOutTransition } from '@libs/ui';

import { DynamicFullScreenMap, MapType } from '../maps';
import { useMapStore } from '../../core/stores/mapStore/mapStore';

import { ActivityDetailsTemplateId } from './models/activityDetailsTemplateId';
import type {
  ActivityStaticData,
  ActivityDynamicData,
  SelectTicketEventPayload,
} from './models';
import ActivityDetailsFooter from './components/ActivityDetailsFooter.vue';
import ActivityDetailsGallery from './components/ActivityDetailsGallery.vue';
import ActivityDetailsDescription from './components/description/ActivityDetailsDescription.vue';
import ActivityDetailsTickets from './components/ticket/ActivityDetailsTickets.vue';
import { useActivityDetailsMapStore } from './stores/activityDetailsMapStore';
import { useActivityDetailsTicketsStore } from './stores/activityDetailsTicketsStore';
import { useActivityDetailsPanelStore } from './stores/activityDetailsPanelStore';
import ActivityDetailsTicketsLoader from './components/loaders/ActivityDetailsTicketsLoader.vue';
import ActivityDetailsGalleryLoader from './components/loaders/ActivityDetailsGalleryLoader.vue';
import ActivityDetailsFooterLoader from './components/loaders/ActivityDetailsFooterLoader.vue';

interface ActivityDetailsPanelProps {
  activityStaticData?: ActivityStaticData;
  activityDynamicData?: ActivityDynamicData;
}

const props = defineProps<ActivityDetailsPanelProps>();

interface ActivityDetailsPanelEmits {
  (event: 'close'): void;
  (eventName: 'ticketSelected', eventPayload: SelectTicketEventPayload): void;
}
const emit = defineEmits<ActivityDetailsPanelEmits>();

const activityDetailsStore = useActivityDetailsPanelStore();
const activityDetailsMapStore = useActivityDetailsMapStore();
const activityDetailsTicketsStore = useActivityDetailsTicketsStore();
const mapStore = useMapStore();

const ticketsSectionRef = ref(null);
const isTicketsSectionVisible = ref(false);

const isFooterVisible = computed(
  () =>
    !isTicketsSectionVisible.value ||
    activityDetailsTicketsStore.isActivitySelected ||
    !activityDetailsTicketsStore.shouldScrollToTicket
);

function scrollToTicket(): void {
  nextTick(() => {
    const element = document.getElementById(
      ActivityDetailsTemplateId.TicketRates
    );
    element?.scrollIntoView({ behavior: 'smooth' });
  });
}

function scrollToTicketWhenAvailable(): void {
  // NOTE: Hack fix to allow the DOM to update with latest status before attempting to scroll to tickets
  // TODO: Find a better solution to wait for the state to be final
  const BUFFER_TIME_TO_SCROLL = 250;

  if (!props.activityDynamicData?.isAvailabilityLoading) {
    setTimeout(() => scrollToTicket(), BUFFER_TIME_TO_SCROLL);
  } else {
    const stop = watch(
      () => props.activityDynamicData?.isAvailabilityLoading,
      (isLoadingAvailability) => {
        if (!isLoadingAvailability) {
          setTimeout(() => scrollToTicket(), BUFFER_TIME_TO_SCROLL);
          stop();
        }
      }
    );
  }
}

useIntersectionObserver(ticketsSectionRef, ([entry]) => {
  if (entry) {
    isTicketsSectionVisible.value = entry.isIntersecting;
  }
});

onMounted(() => {
  document.body.style.overflow = 'hidden';

  if (
    props.activityStaticData?.scrollToAvailability &&
    isLoadingTickets.value
  ) {
    scrollToTicketWhenAvailable();
  }
});

onUnmounted(() => {
  document.body.style.overflow = '';
});

const isLoading = computed(
  () => !props.activityStaticData?.staticInfo || isFeatureToggleActive('loader')
);

const isLoadingGallery = computed(
  () =>
    isFeatureToggleActive('loader') ||
    !props.activityStaticData?.staticInfo.images
);

const isLoadingTickets = computed(
  () =>
    isFeatureToggleActive('loader') ||
    !activityDetailsTicketsStore.ticketRatesInSelectedDate?.length
);
</script>

<template>
  <AppFirstLevelPanel class="activity-details-panel" @close="emit('close')">
    <template #content>
      <div class="content-wrapper">
        <ActivityDetailsGalleryLoader
          v-if="isLoadingGallery"
          @close="emit('close')"
        />
        <ActivityDetailsGallery
          v-else-if="
            activityDetailsStore.headerData &&
            props.activityStaticData?.staticInfo.images
          "
          :gallery-type="activityDetailsStore.galleryType"
          :product-text="activityDetailsStore.headerData.headerText"
          :images="props.activityStaticData?.staticInfo.images"
          :activity-code="props.activityStaticData?.productCode"
          @close="emit('close')"
        />
        <ActivityDetailsDescription :is-loading="isLoading" />

        <ActivityDetailsTicketsLoader v-if="isLoadingTickets" />

        <ActivityDetailsTickets
          v-else-if="
            props.activityDynamicData &&
            props.activityStaticData &&
            !isLoadingTickets
          "
          ref="ticketsSectionRef"
          :product-code="props.activityStaticData?.productCode"
          :activity-type="props.activityStaticData?.staticInfo.typeOfHotel"
          :is-alternative="!!props.activityStaticData?.isAlternative"
        />
      </div>
    </template>
    <template #footer>
      <AppFadeInOutTransition>
        <ActivityDetailsFooterLoader v-if="isLoadingTickets" />

        <ActivityDetailsFooter
          v-else-if="isFooterVisible"
          @close="emit('close')"
          @scroll-to-ticket="scrollToTicket"
        />
      </AppFadeInOutTransition>
    </template>
  </AppFirstLevelPanel>

  <DynamicFullScreenMap
    v-if="activityDetailsMapStore.shouldShowFullscreenMap"
    :map-configuration="{
      markers: activityDetailsMapStore.mapMarker,
      markerFormatters: activityDetailsMapStore.getMapMarkerFormatters(
        MapType.Dynamic,
        mapStore.mapTypeId
      ),
      startRectangle: activityDetailsMapStore.initialMapView,
    }"
    @close="activityDetailsMapStore.closeDynamicMapModal"
  />
</template>

<style scoped lang="scss">
.activity-details-panel {
  background-color: var(--color-white);

  .content-wrapper {
    @include for-more-or-equal-than-900-px {
      padding: var(--spacing-24) var(--spacing-24) var(--spacing-32)
        var(--spacing-24);
    }

    .divider {
      margin: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);
      border-top: 1px solid var(--color-scrollbar);

      @include for-more-or-equal-than-900-px {
        margin: 0 0 var(--spacing-32) 0;
      }
    }
  }
}
::-webkit-scrollbar-track {
  background: var(--color-white);
}
</style>
